import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import {
  CheckSquare,
  Calendar,
  Users,
  Lightbulb,
  Clock,
  TrendingUp,
  ArrowRight,
  ShoppingCart,
} from "lucide-react";
import { format } from "date-fns";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, tasksRes, eventsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/tasks?completed=false`),
        axios.get(`${API}/events`),
      ]);

      setStats(statsRes.data);
      setRecentTasks(tasksRes.data.slice(0, 5));

      // Filter today's events
      const today = format(new Date(), "yyyy-MM-dd");
      const todayEvts = eventsRes.data.filter((e) => e.date === today);
      setTodayEvents(todayEvts.slice(0, 3));
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      label: "Pending Tasks",
      value: stats?.pending_tasks || 0,
      icon: CheckSquare,
      color: "text-amber-400",
      bg: "bg-amber-500/10",
      link: "/tasks",
    },
    {
      label: "Today's Events",
      value: stats?.today_events || 0,
      icon: Calendar,
      color: "text-cyan-400",
      bg: "bg-cyan-500/10",
      link: "/calendar",
    },
    {
      label: "Shopping Items",
      value: stats?.shopping_items || 0,
      icon: ShoppingCart,
      color: "text-emerald-400",
      bg: "bg-emerald-500/10",
      link: "/shopping",
    },
    {
      label: "Lights On",
      value: stats?.lights_on || 0,
      icon: Lightbulb,
      color: "text-yellow-400",
      bg: "bg-yellow-500/10",
      link: "/automation",
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-2">
          Welcome Home
        </h1>
        <p className="text-slate-400">
          {format(new Date(), "EEEE, MMMM d, yyyy")}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Link
            key={stat.label}
            to={stat.link}
            className={`card-base p-5 animate-fade-in stagger-${index + 1}`}
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center mb-3`}>
              <stat.icon className={stat.color} size={20} />
            </div>
            <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
            <p className="text-sm text-slate-400">{stat.label}</p>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Pending Tasks */}
        <div className="lg:col-span-7 card-base p-6 animate-fade-in stagger-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-white font-['Outfit']">
              Pending Tasks
            </h2>
            <Link
              to="/tasks"
              className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
              data-testid="view-all-tasks"
            >
              View all <ArrowRight size={16} />
            </Link>
          </div>

          {recentTasks.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No pending tasks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-4 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-800 transition-colors"
                  data-testid={`task-item-${task.id}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      task.priority === "high"
                        ? "bg-red-500"
                        : task.priority === "medium"
                        ? "bg-amber-500"
                        : "bg-green-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-white truncate">{task.title}</p>
                    {task.assigned_to_name && (
                      <p className="text-xs text-slate-500">
                        Assigned to {task.assigned_to_name}
                      </p>
                    )}
                  </div>
                  {task.due_date && (
                    <span className="text-xs text-slate-500">
                      {format(new Date(task.due_date), "MMM d")}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's Schedule */}
        <div className="lg:col-span-5 card-base p-6 animate-fade-in stagger-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-white font-['Outfit']">
              Today's Schedule
            </h2>
            <Link
              to="/calendar"
              className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
              data-testid="view-calendar"
            >
              Calendar <ArrowRight size={16} />
            </Link>
          </div>

          {todayEvents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No events today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-3 p-3 rounded-xl bg-slate-800/50"
                  data-testid={`event-item-${event.id}`}
                >
                  <div
                    className="w-1 h-full min-h-[40px] rounded-full"
                    style={{ backgroundColor: event.color }}
                  />
                  <div className="flex-1">
                    <p className="text-white font-medium">{event.title}</p>
                    {event.time && (
                      <p className="text-sm text-slate-400 flex items-center gap-1 mt-1">
                        <Clock size={14} />
                        {event.time}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="card-base p-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="text-indigo-400" size={20} />
          <h2 className="text-xl font-semibold text-white font-['Outfit']">
            Quick Overview
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-4 rounded-xl bg-slate-800/50">
            <p className="text-2xl font-bold text-emerald-400">
              {stats?.completed_tasks || 0}
            </p>
            <p className="text-sm text-slate-400">Tasks Completed</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/50">
            <p className="text-2xl font-bold text-indigo-400">
              {stats?.total_tasks || 0}
            </p>
            <p className="text-sm text-slate-400">Total Tasks</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/50">
            <p className="text-2xl font-bold text-cyan-400">
              {stats?.total_events || 0}
            </p>
            <p className="text-sm text-slate-400">Total Events</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-800/50">
            <p className="text-2xl font-bold text-yellow-400">
              {stats?.total_devices || 0}
            </p>
            <p className="text-sm text-slate-400">Smart Devices</p>
          </div>
        </div>
      </div>
    </div>
  );
}
